//#region IMPORT
import React, { useEffect, useRef, useState } from 'react';
import M_FILE_CUSTOM_03 from './M_FILE_CUSTOM_03';
import Global from '../../../../Services/Common/Global';
import Commons from '../../../../Services/Common/Common';
import 'pdfjs-dist/build/pdf.worker.entry';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import dayjs from 'dayjs';
import { useSearch1, useSearch2, useSearch3 } from './M_FILE_CUSTOM_03_Query';
import { PDFDocument } from 'pdf-lib';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { useNavigate } from 'react-router-dom';
//#endregion

//#region 작업허가서 PDF 생성
/** TB_BS_CODE GROUP_CD=WORK_TYPE — CODE_NM → 레포트 필드명 (첨부 코드표와 동일) */
const WORK_TYPE_CODE_NM_TO_WT = {
  일반작업: 'WT_001',
  화기작업: 'WT_002',
  밀폐공간작업: 'WT_003',
  전기작업: 'WT_004',
  고소작업: 'WT_005',
  굴착작업: 'WT_006',
  잠수작업: 'WT_007',
};

/** TB_BS_CODE CODE — headerData workTypeCd{n} 등 */
const WORK_TYPE_WT_TO_CD = {
  WT_001: 'CD_WT_001',
  WT_002: 'CD_WT_002',
  WT_003: 'CD_WT_003',
  WT_004: 'CD_WT_004',
  WT_005: 'CD_WT_005',
  WT_006: 'CD_WT_006',
  WT_007: 'CD_WT_007',
};

/** GetPerNewR3 MST 행: camelCase / PascalCase / UPPER_SNAKE 키 허용 */
function PickDetail(row, ...keys) {
  if (row == null || typeof row !== 'object') return '';
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v !== undefined && v !== null) return v;
  }
  return '';
}

function NormalizePerNewR3Payload(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};

  const mst =
    (p.mst && typeof p.mst === 'object' && !Array.isArray(p.mst) && p.mst) ||
    (p.Mst && typeof p.Mst === 'object' && !Array.isArray(p.Mst) && p.Mst) ||
    (p.MST && typeof p.MST === 'object' && !Array.isArray(p.MST) && p.MST) ||
    null;

  const dailiesRaw =
    p.dailies ?? p.Dailies ?? p.DAILIES ?? p.dailyList ?? p.DailyList ?? [];
  const dailies = Array.isArray(dailiesRaw) ? dailiesRaw : [];

  const centerSignsRaw =
    p.centerSigns ??
    p.CenterSigns ??
    p.CENTER_SIGNS ??
    p.centerSignList ??
    p.CenterSignList ??
    [];
  const centerSigns = Array.isArray(centerSignsRaw) ? centerSignsRaw : [];

  return { mst, dailies, centerSigns };
}

function FlattenPerNewDetails(dailies) {
  const rows = [];
  const dailyArr = Array.isArray(dailies) ? dailies : [];
  for (const daily of dailyArr) {
    if (!daily || typeof daily !== 'object') continue;
    const workDate =
      daily.workDate ??
      daily.WorkDate ??
      daily.WORK_DATE ??
      daily.workYmd ??
      '';
    const dailyCd = daily.dailyCd ?? daily.DailyCd ?? daily.DAILY_CD ?? '';
    const detailsRaw =
      daily.details ??
      daily.Details ??
      daily.DETAILS ??
      daily.detailList ??
      daily.DetailList ??
      daily.permits ??
      daily.Permits ??
      [];
    const details = Array.isArray(detailsRaw) ? detailsRaw : [];
    for (const det of details) {
      if (!det || typeof det !== 'object') continue;
      rows.push({
        ...det,
        workDate,
        dailyCd,
      });
    }
  }
  return rows;
}

/** Detail의 WorkTypeNm 기준으로 해당 작업종류 행만 (종류별 1장 레포트용) */
function FilterPerNewDetailsByWorkTypeNm(detailRows, workTypeNm) {
  if (workTypeNm == null || String(workTypeNm).trim() === '') {
    return detailRows;
  }
  const target = String(workTypeNm).trim();
  return detailRows.filter((d) => String(d.workTypeNm ?? '').trim() === target);
}

/** detail에서 연장 안전관리자 성명·서명경로 — 슬라이스 내 첫 비어 있지 않은 값 */
function PickDetailExtSmFields(details) {
  let extSmNm = '';
  let extSmSignPath = '';
  for (const d of details) {
    const nm = d?.extSmNm ?? '';
    const path = d?.extSmSignPath ?? '';
    const nmT = String(nm ?? '').trim();
    const pathT = String(path ?? '').trim();
    if (!extSmNm && nmT) extSmNm = nmT;
    if (!extSmSignPath && pathT) extSmSignPath = pathT;
    if (extSmNm && extSmSignPath) break;
  }
  return { extSmNm, extSmSignPath };
}

/** detail 행 기타비고 — 조회 컬럼명(camel/snake/대문자) 병기 */
function PickDetailEtcRemark(d) {
  if (!d || typeof d !== 'object') return '';
  const v =
    d.etcRemark ??
    d.EtcRemark ??
    d.etc_remark ??
    d.ETC_REMARK ??
    d.Etc_Remark ??
    '';
  const s = v != null ? String(v).trim() : '';
  return s;
}

/** detail 작업일시 종료: EndTime만 (연장 ExtEndTime 미사용) */
function PickDetailEndTimeForWork(d) {
  return d?.endTime ?? '';
}

/** detail 연장 종료: ExtEndTime만. 없으면 빈값(EndTime으로 채우지 않음) */
function PickDetailEndTimeForExtension(d) {
  const ext = d?.extEndTime ?? '';
  if (ext == null || String(ext).trim() === '') return '';
  return ext;
}

/** Detail StrTime/EndTime — HH:mm 정규화 (목록 그리드 padHm과 동일) */
function PadHmPerNewTime(t) {
  if (t == null || t === '') return '';
  const parts = String(t).split(':');
  const h = parts[0] ?? '00';
  const m = parts[1] ?? '00';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * 작업종류 슬라이스 detail에서 StrTime 최소,
 * 작업 종료(EndTime만) 최대, 연장 종료(ExtEndTime 있는 행만) 최대
 */
function AggregateDetailStrAndEndTimes(details) {
  let minStr = '';
  let maxEndWork = '';
  let maxEndExt = '';
  for (const d of details) {
    const st = d?.strTime;
    const enWork = PickDetailEndTimeForWork(d);
    const enExt = PickDetailEndTimeForExtension(d);
    const stP = PadHmPerNewTime(st) || String(st ?? '').trim();
    const enWorkP = PadHmPerNewTime(enWork) || String(enWork ?? '').trim();
    const enExtP = PadHmPerNewTime(enExt) || String(enExt ?? '').trim();
    if (stP) {
      if (!minStr || stP.localeCompare(minStr) < 0) minStr = stP;
    }
    if (enWorkP) {
      if (!maxEndWork || enWorkP.localeCompare(maxEndWork) > 0) {
        maxEndWork = enWorkP;
      }
    }
    if (enExtP) {
      if (!maxEndExt || enExtP.localeCompare(maxEndExt) > 0) {
        maxEndExt = enExtP;
      }
    }
  }
  return { strTime: minStr, endTime: maxEndWork, extEndTime: maxEndExt };
}

/** daily의 workDate + detail 시각 → 레포트용 `YYYY-MM-DD HH:mm` */
function FormatPerNewDetailYmdHm(dateRaw, hmRaw) {
  const hm = PadHmPerNewTime(hmRaw) || String(hmRaw ?? '').trim();
  if (dateRaw == null || dateRaw === '') {
    return hm || '';
  }
  const d = dayjs(dateRaw);
  const ymd = d.isValid() ? d.format('YYYY-MM-DD') : '';
  if (ymd && hm) return `${ymd} ${hm}`;
  if (ymd) return ymd;
  return hm || '';
}

/**
 * dailies(또는 dailiesByPmCd 병합) 기준 각 detail 행:
 * (workDate+strTime) ~ (workDate+endTime), 복수 행은 ", "로 이어 붙임.
 */
function BuildWorkDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const en = PickDetailEndTimeForWork(d);
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (start) segments.push(start);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

/** ExtEndTime 없는 행은 연장 구간에 포함하지 않음 */
function BuildExtensionDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const en = PickDetailEndTimeForExtension(d);
    if (!en || String(en).trim() === '') continue;
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

function AsPerNewArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [];
}

/** TB_BO_PERMIT_ITEM_* ItemCheck: 위험/장비 등 확인(0) 등 */
function PerNewItemCheckConfirmed(v) {
  return v === '0' || v === 0 || v === true;
}

function MergePerNewRiskItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const risks = AsPerNewArray(det?.riskItems ?? det?.RiskItems);
    for (const ri of risks) {
      const code = String(ri?.riskCode ?? '').trim();
      if (!code) continue;
      const name = ri?.riskNm ?? '';
      const checked = PerNewItemCheckConfirmed(ri?.itemCheck ?? ri?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

function MergePerNewEquipItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.equipItems ?? det?.EquipItems);
    for (const it of items) {
      const code = String(it?.workEquipCode ?? '').trim();
      if (!code) continue;
      const name = it?.workEquipNm ?? '';
      const checked = PerNewItemCheckConfirmed(it?.itemCheck ?? it?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

/** 준비사항 prepItems ItemCheck: '0'=필요(필요 열), '1'=필요 아님(확인 열) */
function PrepItemCheckIsNeed(v) {
  return v === '0' || v === 0;
}

function PrepItemCheckIsNotNeed(v) {
  return v === '1' || v === 1;
}

/** 준비사항 prepItems WitnessCheck: 레포트 '확인' 열 — `0`만 체크, `1` 등은 미체크 */
function PrepWitnessCheckConfirmed(v) {
  if (v === undefined || v === null || v === '') return false;
  if (v === 0 || v === '0') return true;
  const s = String(v).trim();
  return s === '0';
}

/** 레포트용 WitnessCheck 코드 문자열 (`"0"` / `"1"` / 기타·없음 `""`) */
function PrepWitnessCheckReportCode(v) {
  if (v === undefined || v === null || String(v).trim() === '') return '';
  if (v === 0 || v === 1) return v === 0 ? '0' : '1';
  const s = String(v).trim();
  if (s === '0' || s === '1') return s;
  return s;
}

/** Telerik 등 `Fields.PREP_CONFIRMED = "true"` 문자열 비교용 */
function PrepConfirmedReportString(confirmed) {
  return confirmed ? 'true' : 'false';
}
function BuildPreparationTableFromMasterAndDetail(
  masterPreps,
  prepMergedByCode
) {
  const sorted = [...masterPreps].sort((a, b) =>
    String(a.preparationCode ?? a.PreparationCode ?? '').localeCompare(
      String(b.preparationCode ?? b.PreparationCode ?? '')
    )
  );
  return sorted.map((p) => {
    const code = String(p.preparationCode ?? p.PreparationCode ?? '').trim();
    const name = String(p.preparationNm ?? p.PreparationNm ?? '').trim();
    const m = code ? prepMergedByCode.get(code) : null;
    return {
      PREP_CODE: code,
      PREP_NAME: name,
      PREP_NEED: !!(m && m.need),
      PREP_NOT_NEED: !!(m && m.notNeed),
      /** '확인' 열: WitnessCheck — 0만 체크 (레포트는 문자열 "true"/"false") */
      PREP_CONFIRMED: PrepConfirmedReportString(!!(m && m.witnessConfirmed)),
      PREP_WITNESS_CHECK: m
        ? PrepWitnessCheckReportCode(m.witnessCheckRaw)
        : '',
      PREP_CHECKED: !!(m && m.need),
      WORKER_NAME: '',
    };
  });
}

function MergePerNewPrepItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.prepItems ?? det?.PrepItems);
    for (const it of items) {
      const code = String(it?.preparationCode ?? '').trim();
      if (!code) continue;
      const name = it?.preparationNm ?? '';
      const v = it?.itemCheck ?? it?.ItemCheck;
      const isNeed = PrepItemCheckIsNeed(v);
      const isNotNeed = PrepItemCheckIsNotNeed(v);
      const w = it?.witnessCheck ?? it?.WitnessCheck;
      const prev = byCode.get(code);
      const need = prev ? prev.need || isNeed : isNeed;
      const notNeed = prev ? prev.notNeed || isNotNeed : isNotNeed;
      const hasW = w !== undefined && w !== null && String(w).trim() !== '';
      byCode.set(code, {
        code,
        name: prev?.name || name,
        need,
        notNeed,
        /** 선택된 준비사항 목록 등 — 필요('0') 여부 */
        checked: need,
        itemCheckRaw:
          v !== undefined && v !== null && String(v) !== ''
            ? v
            : prev?.itemCheckRaw,
        /** 병합 후 한 번만 판정(OR 금지: 한 줄은 1인데 다른 줄 0이면 잘못 체크됨) */
        witnessCheckRaw: hasW ? w : prev?.witnessCheckRaw,
      });
    }
  }
  const merged = [...byCode.values()].map((e) => ({
    ...e,
    witnessConfirmed: PrepWitnessCheckConfirmed(e.witnessCheckRaw),
  }));
  return merged.sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

/** 레포트 유해위험요인·안전보호구 고정 슬롯 수 (Fields.RISK_n_*, EQUIP_n_*) */
const REPORT_RISK_EQUIP_SLOT_COUNT = 18;

function FindWorkItemIdForSlice(sliceNm, workTypes) {
  const nm = String(sliceNm ?? '').trim();
  if (!nm || !Array.isArray(workTypes)) return '';
  const hit = workTypes.find((w) => {
    const wnm = String(w.workTypeNm ?? w.WorkTypeNm ?? '').trim();
    return wnm === nm;
  });
  return String(hit?.workItemId ?? hit?.WorkItemId ?? '').trim();
}

function BuildRiskSlotsFromMasterAndDetail(masterRisks, mergedByCode) {
  const sorted = [...masterRisks].sort((a, b) =>
    String(a.riskCode ?? a.RiskCode ?? '').localeCompare(
      String(b.riskCode ?? b.RiskCode ?? '')
    )
  );
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    const it = sorted[i - 1];
    const code = it ? String(it.riskCode ?? it.RiskCode ?? '').trim() : '';
    const name = it ? String(it.riskNm ?? it.RiskNm ?? '').trim() : '';
    const m = code && mergedByCode ? mergedByCode.get(code) : null;
    row[`RISK_${i}_CODE`] = code;
    row[`RISK_${i}_NAME`] = name;
    row[`RISK_${i}_CHECKED`] = !!(m && m.checked);
  }
  return row;
}

function BuildEquipSlotsFromMasterAndDetail(masterEquips, mergedByCode) {
  const sorted = [...masterEquips].sort((a, b) =>
    String(a.workEquipCode ?? a.WorkEquipCode ?? '').localeCompare(
      String(b.workEquipCode ?? b.WorkEquipCode ?? '')
    )
  );
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    const it = sorted[i - 1];
    const code = it
      ? String(it.workEquipCode ?? it.WorkEquipCode ?? '').trim()
      : '';
    const name = it
      ? String(it.workEquipNm ?? it.WorkEquipNm ?? '').trim()
      : '';
    const m = code && mergedByCode ? mergedByCode.get(code) : null;
    row[`EQUIP_${i}_CODE`] = code;
    row[`EQUIP_${i}_NAME`] = name;
    row[`EQUIP_${i}_CHECKED`] = !!(m && m.checked);
  }
  return row;
}

function BuildRiskSlotsFallbackFromMergedOnly(mergedList) {
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    row[`RISK_${i}_CODE`] = '';
    row[`RISK_${i}_NAME`] = '';
    row[`RISK_${i}_CHECKED`] = false;
  }
  mergedList.slice(0, REPORT_RISK_EQUIP_SLOT_COUNT).forEach((item, index) => {
    const fieldIndex = index + 1;
    row[`RISK_${fieldIndex}_CHECKED`] = item.checked;
    row[`RISK_${fieldIndex}_NAME`] = item.name || '';
    row[`RISK_${fieldIndex}_CODE`] = item.code || '';
  });
  return row;
}

function BuildEquipSlotsFallbackFromMergedOnly(mergedList) {
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    row[`EQUIP_${i}_CODE`] = '';
    row[`EQUIP_${i}_NAME`] = '';
    row[`EQUIP_${i}_CHECKED`] = false;
  }
  mergedList.slice(0, REPORT_RISK_EQUIP_SLOT_COUNT).forEach((item, index) => {
    const fieldIndex = index + 1;
    row[`EQUIP_${fieldIndex}_CHECKED`] = item.checked;
    row[`EQUIP_${fieldIndex}_NAME`] = item.name || '';
    row[`EQUIP_${fieldIndex}_CODE`] = item.code || '';
  });
  return row;
}

const ConvertImageToBase64 = async (url) => {
  if (!url) return '';
  try {
    const response = await fetch(url);
    if (!response.ok) return '';
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // Base64 문자열 반환
      };
      reader.onerror = reject; // 오류 처리
      reader.readAsDataURL(blob); // Blob을 Base64로 변환
    });
  } catch (error) {
    console.error('이미지 변환 오류:', error);
    return '';
  }
};
//#endregion

function M_FILE_CUSTOM_03F(props) {
  //#region 변수
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const activeStep = useSelector((state) => state.common.activeStep);
  const [loading, setLoading] = useState(false);
  const [fullUrl, setFullUrl] = useState();
  const [renderPreview, setRenderPreview] = useState(null);
  const [numPages, setNumPages] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(window.innerWidth * 1);
  const [pageViewHeight, setPageViewHeight] = useState(0);
  //#endregion
  const perNewR2CatalogRef = useRef(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);
  //#region 이벤트

  //#endregion

  //#region 쿼리
  const { refetch: Search1 } = useSearch1({
    onSuccess: async (rows) => {
      const data = rows[0];
      if (data?.depCd) {
        Search2Async({ depCd: data.depCd });
      }
    },
  });
  const { runAsync: Search2Async } = useSearch2({
    onSuccess: async (rows) => {
      setLoading(true);
      try {
        const pmCds = rows.map((r) => String(r.pmCd).trim()).filter(Boolean);

        if (pmCds.length === 0) {
          setFullUrl(null);
          setRenderPreview(null);
          return;
        }

        const permitBlobs = [];

        const executePermitPdfForPayload = async (payload) => {
          const env = NormalizePerNewR3Payload(payload);
          const rowMst =
            env.mst && typeof env.mst === 'object' && !Array.isArray(env.mst)
              ? env.mst
              : null;

          if (!rowMst) {
            return null;
          }

          const P = (...keys) => PickDetail(rowMst, ...keys);

          const reportData = {
            pmCd: P('pmCd', 'PmCd', 'PM_CD'),
            custCd: P('custCd', 'CustCd', 'CUST_CD'),
            depCd: P('depCd', 'DepCd', 'DEP_CD'),
            reqId: P('reqId', 'ReqId', 'REQ_ID'),
            reqNm: P('reqNm', 'ReqNm', 'REQ_NM'),
            reqDepNm: P('reqDepNm', 'ReqDepNm', 'REQ_DEP_NM'),
            reqRank: P('reqRank', 'ReqRank', 'REQ_RANK'),
            reqPosition: P('reqPosition', 'ReqPosition', 'REQ_POSITION'),
            reqSignPath: P('reqSignPath', 'ReqSignPath', 'REQ_SIGN_PATH'),
            workItems: P('workItems', 'WorkItems', 'WORK_ITEMS'),
            totalStartDate: P(
              'totalStartDate',
              'TotalStartDate',
              'TOTAL_START_DATE'
            ),
            totalEndDate: P('totalEndDate', 'TotalEndDate', 'TOTAL_END_DATE'),
            workLoc: P('workLoc', 'WorkLoc', 'WORK_LOC'),
            workContent: P('workContent', 'WorkContent', 'WORK_CONTENT'),
            workMemberNo: P('workMemberNo', 'WorkMemberNo', 'WORK_MEMBER_NO'),
            partnerNm: P('partnerNm', 'PartnerNm', 'PARTNER_NM'),
            partnerManager: P(
              'partnerManager',
              'PartnerManager',
              'PARTNER_MANAGER'
            ),
            partnerTel: P('partnerTel', 'PartnerTel', 'PARTNER_TEL'),
            workerList: P('workerList', 'WorkerList', 'WORKER_LIST'),
            depSign: P('depSign', 'DepSign', 'DEP_SIGN'),
            smSign: P('smSign', 'SmSign', 'SM_SIGN'),
            smId: P('smId', 'SmId', 'SM_ID'),
            smNm: P('smNm', 'SmNm', 'SM_NM'),
            smDepNm: P('smDepNm', 'SmDepNm', 'SM_DEP_NM'),
            smRank: P('smRank', 'SmRank', 'SM_RANK'),
            smPosition: P('smPosition', 'SmPosition', 'SM_POSITION'),
            smSignPath: P('smSignPath', 'SmSignPath', 'SM_SIGN_PATH'),
            smSignDate: P('smSignDate', 'SmSignDate', 'SM_SIGN_DATE'),
            extSmNm: P('extSmNm', 'ExtSmNm', 'EXT_SM_NM'),
            extSmSignPath: P(
              'extSmSignPath',
              'ExtSmSignPath',
              'EXT_SM_SIGN_PATH'
            ),
            returnYn: P('returnYn', 'ReturnYn', 'RETURN_YN'),
            returnDetail: P('returnDetail', 'ReturnDetail', 'RETURN_DETAIL'),
            returnId: P('returnId', 'ReturnId', 'RETURN_ID'),
            witnessId: P('witnessId', 'WitnessId', 'WITNESS_ID'),
            witnessYn: P('witnessYn', 'WitnessYn', 'WITNESS_YN'),
            witnessNm: P('witnessNm', 'WitnessNm', 'WITNESS_NM'),
            witnessSignPath: P(
              'witnessSignPath',
              'WitnessSignPath',
              'WITNESS_SIGN_PATH'
            ),
            witnessDepNm: P('witnessDepNm', 'WitnessDepNm', 'WITNESS_DEP_NM'),
            witnessRank: P('witnessRank', 'WitnessRank', 'WITNESS_RANK'),
            witnessPosition: P(
              'witnessPosition',
              'WitnessPosition',
              'WITNESS_POSITION'
            ),
            checkRemark: P('checkRemark', 'CheckRemark', 'CHECK_REMARK'),
            status: P('status', 'Status', 'STATUS'),
            createId: P('createId', 'CreateId', 'CREATE_ID'),
            createDate: P('createDate', 'CreateDate', 'CREATE_DATE'),
            updateId: P('updateId', 'UpdateId', 'UPDATE_ID'),
            updateDate: P('updateDate', 'UpdateDate', 'UPDATE_DATE'),
          };
          console.log('============================', reportData);

          // 예외적으로 MST에 camelCase/ PascalCase 혼재로 추가 필드가 올 수 있어 보완
          for (const [k, v] of Object.entries(rowMst)) {
            if (v === undefined || v === null) continue;
            if (
              /^[a-z][a-zA-Z0-9]*$/.test(k) &&
              !Object.prototype.hasOwnProperty.call(reportData, k)
            ) {
              reportData[k] = v;
              continue;
            }
            if (/^[A-Z][a-z]/.test(k)) {
              const camel = k.charAt(0).toLowerCase() + k.slice(1);
              if (!Object.prototype.hasOwnProperty.call(reportData, camel)) {
                reportData[camel] = v;
              }
            }
          }

          const detailRows = FlattenPerNewDetails(env.dailies);

          const executePermitPdf = async (detailPoolRows, options = {}) => {
            const { mode = 'full', selectedRows = [] } = options;

            const formatDateYmd = (dateValue) => {
              if (!dateValue) return '';
              const formatted = dayjs(dateValue).format('YYYY-MM-DD');
              return formatted === 'Invalid Date' ? '' : formatted;
            };

            let smSignBase64 = null;
            const smPath = String(reportData.smSignPath || '').trim();
            if (smPath) {
              const safetyImageUrl =
                Global.file.useUrl + 'files/' + Commons.EncodeUrl(smPath);
              const base64Image = await ConvertImageToBase64(safetyImageUrl);
              smSignBase64 = String(base64Image || '').replace(
                /^data:image\/[a-z]+;base64,/,
                ''
              );
            }

            let reqSignBase64 = null;
            const reqSignPathVal = String(
              reportData.reqSignPath ?? reportData.ReqSignPath ?? ''
            ).trim();
            if (reqSignPathVal) {
              try {
                const reqSignUrl =
                  Global.file.useUrl +
                  'files/' +
                  Commons.EncodeUrl(reqSignPathVal);
                const reqB64 = await ConvertImageToBase64(reqSignUrl);
                reqSignBase64 = String(reqB64 || '').replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                reqSignBase64 = null;
              }
            }

            let witnessSignBase64 = null;
            const witnessSignPathVal = String(
              reportData.witnessSignPath ?? reportData.WitnessSignPath ?? ''
            ).trim();
            if (witnessSignPathVal) {
              try {
                const witnessSignUrl =
                  Global.file.useUrl +
                  'files/' +
                  Commons.EncodeUrl(witnessSignPathVal);
                const witnessB64 = await ConvertImageToBase64(witnessSignUrl);
                witnessSignBase64 = String(witnessB64 || '').replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                witnessSignBase64 = null;
              }
            }

            const createDate = reportData.createDate || '';
            const permitDate = createDate
              ? dayjs(createDate).format('YYYY-MM-DD')
              : '';

            const SplitCommaTrim = (v) =>
              String(v ?? '')
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0);

            const NormalizeWorkTypeToken = (token) => {
              const t = String(token ?? '').trim();
              if (!t) return '';
              return t.replace(/^CD_/i, '');
            };

            const workItemNames = SplitCommaTrim(reportData.workItems);
            const workTypeCodeTokens = SplitCommaTrim(
              reportData.workTypeCds || reportData.workTypes || ''
            ).map(NormalizeWorkTypeToken);

            const emptyWtFlags = {
              WT_001: false,
              WT_002: false,
              WT_003: false,
              WT_004: false,
              WT_005: false,
              WT_006: false,
              WT_007: false,
            };

            const ResolveWorkTypeWtKey = (nm, i) => {
              const code = workTypeCodeTokens[i];
              const fromToken =
                code && Object.prototype.hasOwnProperty.call(emptyWtFlags, code)
                  ? code
                  : '';
              const fromNm = WORK_TYPE_CODE_NM_TO_WT[nm] || '';
              return fromToken || fromNm;
            };

            const workTypeNameJoined =
              workItemNames.length > 0
                ? workItemNames.join(' · ')
                : String(reportData.workItems ?? '')
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .join(' · ');

            const centerTableRows = [];
            for (const cs of env.centerSigns) {
              const path = String(
                cs.centerSignPath ?? cs.CenterSignPath ?? ''
              ).trim();
              let centerB64 = null;
              if (path) {
                try {
                  const u =
                    Global.file.useUrl + 'files/' + Commons.EncodeUrl(path);
                  const rawB64 = await ConvertImageToBase64(u);
                  centerB64 = String(rawB64 || '').replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                } catch {
                  centerB64 = null;
                }
              }
              centerTableRows.push({
                CENTER_ID: cs.centerId ?? cs.CenterId ?? '',
                CENTER_NAME: cs.centerName ?? cs.CenterName ?? '',
                CENTER_RANK: cs.centerRank ?? cs.CenterRank ?? '',
                CENTER_POSITION: cs.centerPosition ?? cs.CenterPosition ?? '',
                CENTER_DEP_NM: cs.centerDepNm ?? cs.CenterDepNm ?? '',
                CENTER_SIGN_IMAGE: centerB64,
                CENTER_UPDATE_YMD: formatDateYmd(
                  cs.updateDate ?? cs.UpdateDate
                ),
              });
            }

            let slices;
            if (mode === 'selection') {
              slices = (selectedRows || []).map((r, idx) => ({
                key: `row-${idx}`,
                dtlCd: String(r?.dtlCd ?? r?.DtlCd ?? '').trim(),
                workTypeNm: String(r?.workTypeNm ?? r?.WorkTypeNm ?? '').trim(),
              }));
              if (slices.length === 0) return null;
            } else {
              slices = workItemNames.length > 0 ? workItemNames : [''];
            }

            const GetSliceKey = (slice) =>
              typeof slice === 'string'
                ? String(slice)
                : String(slice?.key ?? slice?.dtlCd ?? slice?.workTypeNm ?? '');
            const GetSliceNm = (slice) =>
              typeof slice === 'string'
                ? String(slice)
                : String(slice?.workTypeNm ?? '');
            const GetSliceDetails = (slice) => {
              if (mode !== 'selection' || typeof slice === 'string') {
                return FilterPerNewDetailsByWorkTypeNm(detailPoolRows, slice);
              }
              const dtlCd = String(slice?.dtlCd ?? '').trim();
              if (dtlCd) {
                return detailPoolRows.filter(
                  (d) => String(d.dtlCd ?? d.DtlCd ?? '').trim() === dtlCd
                );
              }
              return FilterPerNewDetailsByWorkTypeNm(
                detailPoolRows,
                slice?.workTypeNm
              );
            };

            const mstStartDateYmd = reportData.totalStartDate
              ? formatDateYmd(reportData.totalStartDate)
              : '';
            const mstEndDateYmd = reportData.totalEndDate
              ? formatDateYmd(reportData.totalEndDate)
              : '';

            const extSmSignImageBySlice = {};
            for (const slice of slices) {
              const sliceKey = GetSliceKey(slice);
              const sliceDetailsForExt = GetSliceDetails(slice);
              let extPath =
                PickDetailExtSmFields(sliceDetailsForExt).extSmSignPath;
              extPath = String(extPath ?? '').trim();
              if (!extPath) {
                extPath = String(
                  reportData.extSmSignPath ?? reportData.ExtSmSignPath ?? ''
                ).trim();
              }
              if (extPath) {
                try {
                  const extUrl =
                    Global.file.useUrl + 'files/' + Commons.EncodeUrl(extPath);
                  const rawB64 = await ConvertImageToBase64(extUrl);
                  extSmSignImageBySlice[sliceKey] = String(
                    rawB64 || ''
                  ).replace(/^data:image\/[a-z]+;base64,/, '');
                } catch {
                  extSmSignImageBySlice[sliceKey] = null;
                }
              } else {
                extSmSignImageBySlice[sliceKey] = null;
              }
            }
            const workTypeWitnessSignImageBySlice = {};
            for (const slice of slices) {
              const sliceKey = GetSliceKey(slice);
              const sliceDetailsForWitness = GetSliceDetails(slice);
              let witSignPath = '';
              for (const d of sliceDetailsForWitness) {
                for (const k of [
                  'witnessSignPath',
                  'WitnessSignPath',
                  'WITNESS_SIGN_PATH',
                ]) {
                  const v = d[k];
                  if (v != null && String(v).trim() !== '') {
                    witSignPath = String(v).trim();
                    break;
                  }
                }
                if (witSignPath) break;
              }
              if (witSignPath) {
                try {
                  const witUrl =
                    Global.file.useUrl +
                    'files/' +
                    Commons.EncodeUrl(witSignPath);
                  const witB64 = await ConvertImageToBase64(witUrl);
                  workTypeWitnessSignImageBySlice[sliceKey] = witB64.replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                } catch {
                  workTypeWitnessSignImageBySlice[sliceKey] = null;
                }
              } else {
                workTypeWitnessSignImageBySlice[sliceKey] = null;
              }
            }
            const BuildPrintDataForWorkTypeSlice = (slice) => {
              const sliceNm = GetSliceNm(slice);
              const sliceKey = GetSliceKey(slice);
              const detailsSlice = GetSliceDetails(slice);
              const { extSmNm: detExtSmNm, extSmSignPath: detExtSmSignPath } =
                PickDetailExtSmFields(detailsSlice);
              //작업 시작·종료(EndTime) / 연장 종료(ExtEndTime 있는 행만) 집계
              const {
                strTime: sliceStrTime,
                endTime: sliceEndTime,
                extEndTime: sliceExtEndTime,
              } = AggregateDetailStrAndEndTimes(detailsSlice);
              const workDateTimeFromDetails =
                BuildWorkDateTimeTextFromDetails(detailsSlice);
              const extensionDateTimeFromDetails =
                BuildExtensionDateTimeTextFromDetails(detailsSlice);
              const workDateTimeText =
                workDateTimeFromDetails ||
                [
                  [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                  [mstEndDateYmd, sliceEndTime].filter(Boolean).join(' '),
                ]
                  .filter((s) => s.length > 0)
                  .join(' ~ ');
              const extensionDateTimeText =
                extensionDateTimeFromDetails ||
                (String(sliceExtEndTime ?? '').trim()
                  ? [
                    [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                    [mstEndDateYmd, sliceExtEndTime]
                      .filter(Boolean)
                      .join(' '),
                  ]
                    .filter((s) => s.length > 0)
                    .join(' ~ ')
                  : '');

              const riskMergedPage = MergePerNewRiskItems(detailsSlice);
              const equipMergedPage = MergePerNewEquipItems(detailsSlice);
              const prepMergedPage = MergePerNewPrepItems(detailsSlice);

              const catalog = perNewR2CatalogRef.current;
              const workItemId = FindWorkItemIdForSlice(
                sliceNm,
                catalog?.workTypes
              );
              const riskMergedMap = new Map(
                riskMergedPage.map((x) => [x.code, x])
              );
              const equipMergedMap = new Map(
                equipMergedPage.map((x) => [x.code, x])
              );
              const prepMergedMap = new Map(
                prepMergedPage.map((x) => [x.code, x])
              );

              let riskRowData;
              let equipmentRowData;
              let preparationTableData;

              if (
                workItemId &&
                catalog &&
                Array.isArray(catalog.riskCodes) &&
                catalog.riskCodes.length > 0
              ) {
                const risksForWt = catalog.riskCodes.filter(
                  (r) =>
                    String(r.workItemId ?? r.WorkItemId ?? '') === workItemId
                );
                const equipsForWt = (catalog.equipCodes ?? []).filter(
                  (e) =>
                    String(e.workItemId ?? e.WorkItemId ?? '') === workItemId
                );
                const prepsForWt = (catalog.preparationCodes ?? []).filter(
                  (p) =>
                    String(p.workItemId ?? p.WorkItemId ?? '') === workItemId
                );
                riskRowData =
                  risksForWt.length > 0
                    ? BuildRiskSlotsFromMasterAndDetail(
                      risksForWt,
                      riskMergedMap
                    )
                    : BuildRiskSlotsFallbackFromMergedOnly(riskMergedPage);
                equipmentRowData =
                  equipsForWt.length > 0
                    ? BuildEquipSlotsFromMasterAndDetail(
                      equipsForWt,
                      equipMergedMap
                    )
                    : BuildEquipSlotsFallbackFromMergedOnly(equipMergedPage);
                preparationTableData =
                  prepsForWt.length > 0
                    ? BuildPreparationTableFromMasterAndDetail(
                      prepsForWt,
                      prepMergedMap
                    )
                    : prepMergedPage
                      .filter((item) => item && item.code && item.name)
                      .map((prepItem) => ({
                        PREP_CODE: prepItem.code || '',
                        PREP_NAME: prepItem.name || '',
                        PREP_NEED: !!prepItem.need,
                        PREP_NOT_NEED: !!prepItem.notNeed,
                        PREP_CONFIRMED: PrepConfirmedReportString(
                          !!prepItem.witnessConfirmed
                        ),
                        PREP_WITNESS_CHECK: PrepWitnessCheckReportCode(
                          prepItem.witnessCheckRaw
                        ),
                        PREP_CHECKED: !!prepItem.need,
                        WORKER_NAME: '',
                      }));
              } else {
                riskRowData =
                  BuildRiskSlotsFallbackFromMergedOnly(riskMergedPage);
                equipmentRowData =
                  BuildEquipSlotsFallbackFromMergedOnly(equipMergedPage);
                const validPreparationItems = prepMergedPage.filter(
                  (item) => item && item.code && item.name
                );
                preparationTableData = validPreparationItems.map(
                  (prepItem) => ({
                    PREP_CODE: prepItem.code || '',
                    PREP_NAME: prepItem.name || '',
                    PREP_NEED: !!prepItem.need,
                    PREP_NOT_NEED: !!prepItem.notNeed,
                    PREP_CONFIRMED: PrepConfirmedReportString(
                      !!prepItem.witnessConfirmed
                    ),
                    PREP_WITNESS_CHECK: PrepWitnessCheckReportCode(
                      prepItem.witnessCheckRaw
                    ),
                    PREP_CHECKED: !!prepItem.need,
                    WORKER_NAME: '',
                  })
                );
              }

              const riskTableDataHorizontal = [riskRowData];
              const equipmentTableDataHorizontal = [equipmentRowData];

              const titleNm = sliceNm || workTypeNameJoined;
              const idxInList = sliceNm
                ? workItemNames.findIndex((n) => n === sliceNm)
                : -1;
              const wtKey =
                idxInList >= 0
                  ? ResolveWorkTypeWtKey(sliceNm, idxInList)
                  : sliceNm
                    ? WORK_TYPE_CODE_NM_TO_WT[sliceNm] || ''
                    : '';

              const pageWtFlags = { ...emptyWtFlags };
              if (
                wtKey &&
                Object.prototype.hasOwnProperty.call(pageWtFlags, wtKey)
              ) {
                pageWtFlags[wtKey] = true;
              }

              const TableDataMain = [
                {
                  ...emptyWtFlags,
                  WORK_ITEM_NM: sliceNm,
                  WORK_TYPE_NM: sliceNm,
                  LINE_NO: 1,
                  /** MST 일자 + StrTime/EndTime(작업) — 연장은 EXTENSION_DATE_TIME */
                  WORK_DATE_TIME: workDateTimeText,
                  WorkDateTime: workDateTimeText,
                  EXTENSION_DATE_TIME: extensionDateTimeText,
                  ExtensionDateTime: extensionDateTimeText,
                  ...(wtKey ? { [wtKey]: true } : {}),
                },
              ];

              const selectedRiskNames = riskMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const selectedEquipmentNames = equipMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const selectedPreparationNames = prepMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const etcRemarkPage = detailsSlice
                .map((d) => PickDetailEtcRemark(d))
                .filter((s) => String(s).trim() !== '')
                .join(' / ');
              const checkRemarkPage = detailsSlice
                .map(
                  (d) => d.checkRemark ?? d.CheckRemark ?? d.CHECK_REMARK ?? ''
                )
                .filter(Boolean)
                .join(' / ');

              /** 상세(detail) 행의 증인 — MST witnessNm·witnessRank 등과 별도 */
              const pickDetailWitnessField = (d, keys) => {
                for (const k of keys) {
                  const v = d[k];
                  if (v != null && String(v).trim() !== '')
                    return String(v).trim();
                }
                return '';
              };
              const mergeDetailWitnessUnique = (keys) =>
                [
                  ...new Set(
                    detailsSlice
                      .map((d) => pickDetailWitnessField(d, keys))
                      .filter(Boolean)
                  ),
                ].join(' / ');
              const workTypeWitnessNm = mergeDetailWitnessUnique(['witnessNm']);
              const workTypeWitnessRank = mergeDetailWitnessUnique([
                'witnessRank',
              ]);
              const workTypeWitnessPosition = mergeDetailWitnessUnique([
                'witnessPosition',
              ]);
              const workTypeWitnessDepNm = mergeDetailWitnessUnique([
                'witnessDepNm',
              ]);
              const workTypeWitnessSignPath = mergeDetailWitnessUnique([
                'witnessSignPath',
                'WitnessSignPath',
                'WITNESS_SIGN_PATH',
              ]);

              const headerData = {
                pmCd: reportData.pmCd ?? '',
                custCd: reportData.custCd ?? '',
                depCd: reportData.depCd ?? '',
                reqId: reportData.reqId ?? '',
                reqNm: reportData.reqNm ?? '',
                reqDepNm: reportData.reqDepNm ?? '',
                reqRank: reportData.reqRank ?? '',
                reqPosition: reportData.reqPosition ?? '',
                reqSignPath: reportData.reqSignPath ?? '',
                ReqSignPath:
                  reportData.reqSignPath ?? reportData.ReqSignPath ?? '',
                workItems: sliceNm || reportData.workItems || '',
                WORK_TYPE_NAME: titleNm,
                workTypeName: titleNm,
                totalStartDate: mstStartDateYmd,
                totalEndDate: mstEndDateYmd,
                strTime: sliceStrTime,
                steTime: sliceStrTime,
                endTime: sliceEndTime,
                extEndTime: sliceExtEndTime,
                workDateTimeText,
                extensionDateTimeText,
                WORK_DATE_TIME: workDateTimeText,
                WorkDateTime: workDateTimeText,
                EXTENSION_DATE_TIME: extensionDateTimeText,
                ExtensionDateTime: extensionDateTimeText,
                extSmNm:
                  String(detExtSmNm ?? '').trim() ||
                  String(reportData.extSmNm ?? '').trim() ||
                  '',
                extSmSignPath:
                  String(detExtSmSignPath ?? '').trim() ||
                  String(reportData.extSmSignPath ?? '').trim() ||
                  '',
                workLoc: reportData.workLoc ?? '',
                workContent: reportData.workContent ?? '',
                workMemberNo: reportData.workMemberNo ?? '',
                partnerNm: reportData.partnerNm ?? '',
                partnerManager: reportData.partnerManager ?? '',
                partnerMan: reportData.partnerManager ?? '',
                partnerTel: reportData.partnerTel ?? '',
                workerList: reportData.workerList ?? '',
                depSign: reportData.depSign ?? '',
                smSign: reportData.smSign ?? '',
                smId: reportData.smId ?? '',
                smNm: reportData.smNm ?? '',
                smDepNm: reportData.smDepNm ?? '',
                smRank: reportData.smRank ?? '',
                smPosition: reportData.smPosition ?? '',
                smSignPath: reportData.smSignPath ?? '',
                smSignDate: reportData.smSignDate ?? '',
                returnYn: reportData.returnYn ?? '',
                returnDetail: reportData.returnDetail ?? '',
                returnId: reportData.returnId ?? '',
                witnessId: reportData.witnessId ?? '',
                witnessYn: reportData.witnessYn ?? '',
                witnessNm: reportData.witnessNm ?? '',
                witnessSignPath: reportData.witnessSignPath ?? '',
                WitnessSignPath:
                  reportData.witnessSignPath ??
                  reportData.WitnessSignPath ??
                  '',
                witnessDepNm: reportData.witnessDepNm ?? '',
                witnessRank: reportData.witnessRank ?? '',
                witnessPosition: reportData.witnessPosition ?? '',
                workTypeWitnessNm,
                workTypeWitnessRank,
                workTypeWitnessPosition,
                workTypeWitnessDepNm,
                workTypeWitnessSignPath,
                checkRemark: checkRemarkPage || '',
                status: reportData.status ?? '',
                createId: reportData.createId ?? '',
                createDate: reportData.createDate ?? '',
                updateId: reportData.updateId ?? '',
                updateDate: reportData.updateDate ?? '',

                permitDate,
                workItemCount: sliceNm ? 1 : workItemNames.length,
                ...pageWtFlags,

                // 서명 이미지 - 레포트에 바인딩하는 명칭
                REQ_SIGN_IMAGE: reqSignBase64 || null,
                SM_SIGN_IMAGE: smSignBase64 || null,
                EXT_SM_SIGN_IMAGE: extSmSignImageBySlice[sliceKey] || null,
                WITNESS_SIGN_IMAGE: witnessSignBase64 || null,
                /** 상세(detail) 작업종별 증인 서명 — MST WITNESS_SIGN_IMAGE와 별도 */
                WORKTYPE_WITNESS_SIGN_IMAGE:
                  workTypeWitnessSignImageBySlice[sliceKey] || null,

                smSignDateYmd: formatDateYmd(reportData.smSignDate),
                selectedRiskNames,
                selectedEquipmentNames,
                selectedPreparationNames,
                etcRemark: etcRemarkPage,
                etc_remark: etcRemarkPage,
                ETC_REMARK: etcRemarkPage,
                detailRowCount: detailsSlice.length,
                dailyCount: env.dailies.length,
                centerSignCount: env.centerSigns.length,
              };

              if (sliceNm) {
                headerData.workItemNm1 = sliceNm;
                headerData.workTypeNm1 = sliceNm;
                if (wtKey) {
                  headerData.workTypeWt1 = wtKey;
                  headerData.workTypeCd1 = WORK_TYPE_WT_TO_CD[wtKey] || '';
                }
              } else {
                workItemNames.forEach((nm, i) => {
                  const idx = i + 1;
                  const wk = ResolveWorkTypeWtKey(nm, i);
                  headerData[`workItemNm${idx}`] = nm;
                  headerData[`workTypeNm${idx}`] = nm;
                  if (wk) {
                    headerData[`workTypeWt${idx}`] = wk;
                    headerData[`workTypeCd${idx}`] =
                      WORK_TYPE_WT_TO_CD[wk] || '';
                  }
                });
              }
              return {
                FileName: 'PER_NEW',
                HeaderData: JSON.stringify(headerData),
                TableDataMain,
                TableData1: JSON.stringify(riskTableDataHorizontal),
                TableData2: JSON.stringify(equipmentTableDataHorizontal),
                TableData3: JSON.stringify(preparationTableData),
                TableData4: JSON.stringify(centerTableRows),
                TableData5: JSON.stringify([]),
                TableData6: JSON.stringify([]),
              };
            };

            const pdfBlobs = [];
            for (const slice of slices) {
              const printData = BuildPrintDataForWorkTypeSlice(slice);
              const response = await Commons.Axios(
                'report',
                Global.base.useUrl + Global.urls.Reports.reports,
                printData
              );
              pdfBlobs.push(response.data);
            }

            if (pdfBlobs.length === 0) return null;

            let finalBlob = pdfBlobs[0];
            if (pdfBlobs.length > 1) {
              const mergedPdf = await PDFDocument.create();
              for (const b of pdfBlobs) {
                const pdf = await PDFDocument.load(await b.arrayBuffer());
                const copied = await mergedPdf.copyPages(
                  pdf,
                  pdf.getPageIndices()
                );
                copied.forEach((p) => mergedPdf.addPage(p));
              }
              const mergedBytes = await mergedPdf.save();
              finalBlob = new Blob([mergedBytes], { type: 'application/pdf' });
            }

            return finalBlob;
          };

          return await executePermitPdf(detailRows, { mode: 'full' });
        };

        for (const pmCd of pmCds) {
          const detailsPayloadRows = await Search3Async({ pmCd });
          const payload = detailsPayloadRows[0] ?? null;
          console.log('======payload', payload);

          const permitBlob = payload
            ? await executePermitPdfForPayload(payload)
            : null;
          if (permitBlob) {
            permitBlobs.push(permitBlob);
          }
        }

        if (permitBlobs.length === 0) {
          dispatch(
            showSnackbar({
              message: '작업허가서 PDF를 생성할 데이터가 없습니다.',
              type: 'warning',
            })
          );
          setFullUrl(null);
          setRenderPreview(null);
          return;
        }

        let finalPermitBlob = permitBlobs[0];
        if (permitBlobs.length > 1) {
          const mergedPdf = await PDFDocument.create();
          for (const b of permitBlobs) {
            const pdf = await PDFDocument.load(await b.arrayBuffer());
            const copied = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copied.forEach((p) => mergedPdf.addPage(p));
          }
          const mergedBytes = await mergedPdf.save();
          finalPermitBlob = new Blob([mergedBytes], {
            type: 'application/pdf',
          });
        }

        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blobUrl = URL.createObjectURL(finalPermitBlob);
        blobUrlRef.current = blobUrl;
        setFullUrl(blobUrl);
        // Blob URL 직접 전달 - base64 변환 불필요 (메모리 절약)
        setRenderPreview(blobUrl);
      } catch (e) {
        console.log(e);
        setFullUrl(null);
        setRenderPreview(null);
      } finally {
        setLoading(false);
      }
    },
  });
  const { runAsync: Search3Async } = useSearch3();
  //#endregion

  //#region 나머지 함수
  const getExtensionFromUrl = (url = '') => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      return 'pdf';
    }
    const parts = url?.split('.');
    return parts?.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const onDocumentLoadSuccess = ({ numPages: total }) => setNumPages(total);

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    } else {
      dispatch(CommonActions.setActiveStep(activeStep - 1));
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    } else {
      navigate('/Home');
    }
  };

  //#endregion

  return (
    <M_FILE_CUSTOM_03
      fullUrl={fullUrl}
      renderPreview={renderPreview}
      loading={loading}
      pageNumber={pageNumber}
      pageWidth={pageWidth}
      numPages={numPages}
      pageViewHeight={pageViewHeight}
      setPageViewHeight={setPageViewHeight}
      activeStep={activeStep}
      setActiveStep={(step) => dispatch(CommonActions.setActiveStep(step))}
      getExtensionFromUrl={getExtensionFromUrl}
      onDocumentLoadSuccess={onDocumentLoadSuccess}
      goToPrevPage={goToPrevPage}
      goToNextPage={goToNextPage}
    />
  );
}

export default M_FILE_CUSTOM_03F;
